import React, { useState } from 'react';
import { Table, Tag, Empty, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EmployeeSalary, SalaryItem } from '../../../services/pw9531Api';

interface ResultTableProps {
  results: EmployeeSalary[] | null;
  processed: number;
  loading: boolean;
}

const itemColumns: ColumnsType<SalaryItem> = [
  {
    title: '項目代碼',
    dataIndex: 'itemCode',
    key: 'itemCode',
    width: 100,
  },
  {
    title: '項目名稱',
    dataIndex: 'itemName',
    key: 'itemName',
    width: 150,
  },
  {
    title: '類別',
    dataIndex: 'itemType',
    key: 'itemType',
    width: 80,
    render: (v: string) => (
      <Tag color={v === 'D' ? 'red' : 'green'}>
        {v === 'D' ? '扣除' : '加項'}
      </Tag>
    ),
  },
  {
    title: '金額',
    dataIndex: 'amount',
    key: 'amount',
    width: 120,
    align: 'right' as const,
    render: (v: number) => v.toLocaleString('zh-TW'),
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ results, processed, loading }) => {
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState<EmployeeSalary | null>(null);

  if (!results) {
    return <Empty description="請先執行預覽或薪資計算" />;
  }

  const columns: ColumnsType<EmployeeSalary> = [
    {
      title: '員工編號',
      dataIndex: 'empCode',
      key: 'empCode',
      width: 100,
      sorter: (a, b) => a.empCode.localeCompare(b.empCode),
    },
    {
      title: '員工姓名',
      dataIndex: 'empName',
      key: 'empName',
      width: 120,
    },
    {
      title: '部門',
      dataIndex: 'deptCode',
      key: 'deptCode',
      width: 80,
    },
    {
      title: '應發合計',
      dataIndex: 'grossPay',
      key: 'grossPay',
      width: 120,
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ color: '#389e0d', fontWeight: 'bold' }}>
          {v.toLocaleString('zh-TW')}
        </span>
      ),
    },
    {
      title: '扣款合計',
      dataIndex: 'deductions',
      key: 'deductions',
      width: 120,
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ color: v > 0 ? '#cf1322' : undefined }}>
          {v.toLocaleString('zh-TW')}
        </span>
      ),
    },
    {
      title: '實發金額',
      dataIndex: 'netPay',
      key: 'netPay',
      width: 120,
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ fontWeight: 'bold' }}>
          {v.toLocaleString('zh-TW')}
        </span>
      ),
    },
    {
      title: '明細',
      key: 'action',
      width: 80,
      render: (_: unknown, record: EmployeeSalary) => (
        <a onClick={() => { setSelectedEmp(record); setDetailVisible(true); }}>
          查看
        </a>
      ),
    },
  ];

  // Calculate totals
  const totalGross = results.reduce((sum, r) => sum + r.grossPay, 0);
  const totalDeductions = results.reduce((sum, r) => sum + r.deductions, 0);
  const totalNet = results.reduce((sum, r) => sum + r.netPay, 0);

  return (
    <div>
      <div style={{ marginBottom: 8, color: '#666' }}>
        共處理 <strong>{processed}</strong> 位員工，
        產生 <strong>{results.length}</strong> 筆結果
        {results.length > 0 && (
          <span style={{ marginLeft: 16 }}>
            | 應發合計: <strong style={{ color: '#389e0d' }}>{totalGross.toLocaleString('zh-TW')}</strong>
            {' '}扣款合計: <strong style={{ color: '#cf1322' }}>{totalDeductions.toLocaleString('zh-TW')}</strong>
            {' '}實發合計: <strong>{totalNet.toLocaleString('zh-TW')}</strong>
          </span>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={results}
        rowKey={(r) => r.empCode}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
        scroll={{ y: 500 }}
      />

      <Modal
        title={selectedEmp ? `${selectedEmp.empCode} ${selectedEmp.empName} - 薪資明細` : '薪資明細'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {selectedEmp && (
          <Table
            columns={itemColumns}
            dataSource={selectedEmp.items}
            rowKey={(r) => r.itemCode}
            size="small"
            pagination={false}
          />
        )}
      </Modal>
    </div>
  );
};

export default ResultTable;
